import { useInView } from '@/hooks/useInView';
import { useCountUp, parseStatValue } from '@/hooks/useCountUp';
import { Award, BookOpen, GraduationCap, Users, Monitor, ArrowRight, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

// Count-up stat component for mobile
const MobileCountUpStat = ({ value, label, color, index }: { value: string; label: string; color: string; index: number }) => {
  const { numericValue, prefix, suffix } = parseStatValue(value);
  const { ref, formattedValue, hasStarted } = useCountUp({ 
    end: numericValue, 
    duration: 1500 + index * 200,
    prefix,
    suffix
  });

  return (
    <div 
      className="bg-card rounded-2xl p-4 border border-border/50 shadow-sm text-center"
    >
      <p 
        ref={ref as React.RefObject<HTMLParagraphElement>}
        className={`text-2xl font-bold ${color} transition-all duration-300 ${hasStarted ? 'scale-100' : 'scale-90'}`}
      >
        {hasStarted ? formattedValue : '0'}
      </p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
};

// Mobile Components
export const WhyChooseMobileHeader = () => {
  return (
    <div className="text-center space-y-4 mb-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-primary font-medium text-xs">
        <Award className="h-3.5 w-3.5" />
        Trusted by 2,000+ Families
      </div>
      <h2 className="text-2xl font-heading font-bold text-foreground">
        Why Parents Choose <span className="text-primary">Schoolola</span>
      </h2>
      <p className="text-muted-foreground text-sm">
        Australia's leading primary education specialists
      </p>
    </div>
  );
};

export const WhyChooseMobileStats = () => {
  const stats = [
    { value: '2,000+', label: 'Students Taught', color: 'text-accent' },
    { value: '9', label: 'Centres Across AU', color: 'text-primary' },
    { value: '50+', label: 'Expert Tutors', color: 'text-accent' },
  ];
  
  return (
    <div className="grid grid-cols-2 gap-3 mb-8">
      {stats.map((stat, index) => (
        <MobileCountUpStat key={index} {...stat} index={index} />
      ))}
    </div>
  );
};

export const WhyChooseMobileTmrw = () => {
  return (
    <div className="relative bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-6 text-white overflow-hidden mb-6">
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
      <div className="relative z-10">
        <div className="inline-flex items-center justify-center h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl mb-4">
          <BookOpen className="h-5 w-5" />
        </div>
        <h3 className="text-xl font-heading font-bold mb-2">TMRW™ Methodology</h3>
        <p className="text-white/90 text-sm leading-relaxed mb-4">
          Our <strong>Think, Make, Reason, Write</strong> approach builds critical thinking skills that last a lifetime.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">Critical Thinking</span>
          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">Problem Solving</span>
        </div>
      </div>
    </div>
  );
};

export const WhyChooseMobileFeatures = () => {
  const features = [
    { icon: GraduationCap, title: 'Expert Teachers', desc: 'Qualified primary education specialists', iconColor: 'text-primary', bgFrom: 'from-primary/10', bgTo: 'to-accent/10' },
    { icon: Users, title: 'Small Classes', desc: 'Max 8 students per class', iconColor: 'text-accent', bgFrom: 'from-accent/10', bgTo: 'to-primary/10' },
    { icon: Monitor, title: 'Flexible Options', desc: 'In-centre & online tutoring', iconColor: 'text-primary', bgFrom: 'from-primary/10', bgTo: 'to-accent/10' },
    { icon: Award, title: 'Primary & Early Secondary Specialists', desc: 'Specialist in primary and early secondary tuition (Kindergarten, Prep or Pre-primary to Year 8)', iconColor: 'text-accent', bgFrom: 'from-accent/10', bgTo: 'to-primary/10' },
  ];
  
  return (
    <div className="grid grid-cols-2 gap-3 mb-8">
      {features.map((feature, index) => (
        <div 
          key={index}
          className="bg-card rounded-xl p-4 border border-border/50 shadow-sm"
        >
          <div className={`h-9 w-9 bg-gradient-to-br ${feature.bgFrom} ${feature.bgTo} rounded-lg flex items-center justify-center mb-3`}>
            <feature.icon className={`h-4 w-4 ${feature.iconColor}`} />
          </div>
          <h3 className="text-sm font-heading font-semibold text-foreground mb-1">{feature.title}</h3>
          <p className="text-muted-foreground text-xs leading-relaxed">{feature.desc}</p>
        </div>
      ))}
    </div>
  );
};

export const WhyChooseMobileCta = () => {
  return (
    <div className="text-center">
      <Link to="/book-assessment">
        <Button className="w-full rounded-full shadow-lg bg-white text-primary hover:bg-white/90 border border-primary/20">
          Book Free Assessment
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
};

// Desktop Components
export const WhyChooseDesktopHeader = () => {
  return (
    <div className="text-center space-y-6 mb-20">
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary font-medium text-sm">
        <Award className="h-4 w-4" />
        Trusted by 2,000+ Families
      </div>
      <h2 className="text-4xl lg:text-5xl font-heading font-bold text-foreground">
        Why Parents Choose <span className="text-primary">Schoolola</span>
      </h2>
      <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
        Australia's leading primary education specialists with a proven track record of academic excellence
      </p>
    </div>
  );
};

export const WhyChooseDesktopTmrwCard = () => {
  return (
    <div className="group relative bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-8 lg:p-10 text-white overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
      <div className="relative z-10">
        <div className="inline-flex items-center justify-center h-14 w-14 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
          <BookOpen className="h-7 w-7" />
        </div>
        <h3 className="text-2xl lg:text-3xl font-heading font-bold mb-4">
          TMRW™ Methodology
        </h3>
        <p className="text-white/90 text-lg leading-relaxed mb-6">
          Our revolutionary <strong>Think, Make, Reason, Write</strong> approach transforms how students learn. Developed through years of research, it builds critical thinking and problem-solving skills that last a lifetime.
        </p>
        <div className="flex flex-wrap gap-3">
          <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">Critical Thinking</span>
          <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">Problem Solving</span>
          <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">Confidence Building</span>
        </div>
      </div>
    </div>
  );
};

// Desktop count-up stat component
const DesktopCountUpStat = ({ value, label, color, index }: { 
  value: string; 
  label: string; 
  color: string; 
  index: number;
}) => {
  const { numericValue, prefix, suffix } = parseStatValue(value);
  const { ref, formattedValue, hasStarted } = useCountUp({ 
    end: numericValue, 
    duration: 1800 + index * 200,
    prefix,
    suffix,
    startOnView: true
  });

  return (
    <div className="space-y-1">
      <p 
        ref={ref as React.RefObject<HTMLParagraphElement>}
        className={`text-4xl font-bold ${color}`}
      >
        {hasStarted ? formattedValue : '0'}
      </p>
      <p className="text-muted-foreground">{label}</p>
    </div>
  );
};

export const WhyChooseDesktopStatsCard = () => {
  const stats = [
    { value: '2,000+', label: 'Students Taught', color: 'text-accent' },
    { value: '9', label: 'Centres Across AU', color: 'text-primary' },
    { value: '50+', label: 'Expert Tutors', color: 'text-accent' },
  ];
  
  return (
    <div className="bg-card rounded-3xl p-8 lg:p-10 border border-border/50 shadow-lg">
      <div className="inline-flex items-center justify-center h-14 w-14 bg-accent/10 rounded-2xl mb-6">
        <Target className="h-7 w-7 text-accent" />
      </div>
      <h3 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-6">
        Proven Results
      </h3>
      <div className="grid grid-cols-2 gap-6">
        {stats.map((stat, index) => (
          <DesktopCountUpStat key={index} {...stat} index={index} />
        ))}
      </div>
    </div>
  );
};

export const WhyChooseDesktopFeatureCard = ({ 
  icon: Icon, 
  title, 
  description, 
  iconColor, 
  bgFrom, 
  bgTo 
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string; 
  iconColor: string; 
  bgFrom: string; 
  bgTo: string; 
  delay?: number;
}) => {
  return (
    <div className="group bg-card rounded-2xl p-6 border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-shadow duration-300">
      <div className={`h-12 w-12 bg-gradient-to-br ${bgFrom} ${bgTo} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className={`h-6 w-6 ${iconColor}`} />
      </div>
      <h3 className="text-lg font-heading font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </div>
  );
};

export const WhyChooseDesktopCta = () => {
  return (
    <div className="mt-8 text-center">
      <Link to="/book-assessment">
        <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-shadow bg-white text-primary hover:bg-white/90 border border-primary/20">
          Book Free Assessment
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </Link>
    </div>
  );
};
